export interface ConnectionFlowData {
    min?: number
    max?: number
    value?: number
    animation_enabled?: boolean
}

export interface Point { x: number; y: number }

interface LinePrimitive { type: 'line'; from: Point; to: Point; length: number }
interface ArcPrimitive {
    type: 'arc'; center: Point; radius: number; startAngle: number; deltaAngle: number; length: number
}
type PathPrimitive = LinePrimitive | ArcPrimitive

export interface RoundedConnectionPath { primitives: PathPrimitive[]; length: number }

const finite = (value: unknown, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const clamp01 = (value: number) => Math.max(0, Math.min(1, value))

export const CONNECTION_SPEED_FACTOR_SHIFT = 16

export function normalizeConnectionMaxSpeed(value: unknown): number {
    const speed = finite(value, 120)
    return speed > 0 ? speed : 120
}

export function resolveConnectionFlow(data: ConnectionFlowData, connectionMaxSpeed = 120) {
    const value = finite(data.value)
    const min = Math.min(finite(data.min), finite(data.max, 100))
    const max = Math.max(finite(data.min), finite(data.max, 100))
    const range = max - min
    const normalized = range > 0 ? clamp01((value - min) / range) : 0
    const maximumSpeed = normalizeConnectionMaxSpeed(connectionMaxSpeed)
    const moving = data.animation_enabled !== false && normalized > 0
    return {
        min, max, value,
        normalized,
        speed: moving ? normalized * maximumSpeed : 0,
        moving,
    }
}

export function connectionSpeedFactorQ16(min: number, max: number, connectionMaxSpeed: number): number {
    const range = Number(max) - Number(min)
    if (!Number.isFinite(range) || range <= 0) return 0
    return Math.round(normalizeConnectionMaxSpeed(connectionMaxSpeed) * (1 << CONNECTION_SPEED_FACTOR_SHIFT) / range)
}

export function exportConnectionMotionPath(path: RoundedConnectionPath) {
    return path.primitives.map(primitive => {
        if (primitive.type === 'line') return {
            type: 'line', from: primitive.from, to: primitive.to,
        }
        return {
            type: 'quarter_corner', center: primitive.center, radius: primitive.radius,
            start_quarter: Math.round(primitive.startAngle / (Math.PI / 2)),
            turn: primitive.deltaAngle > 0 ? 1 : -1,
        }
    })
}

function line(from: Point, to: Point): LinePrimitive {
    return { type: 'line', from, to, length: Math.hypot(to.x - from.x, to.y - from.y) }
}

export function buildRoundedConnectionPath(points: Point[], cornerRadius: number): RoundedConnectionPath {
    if (!Array.isArray(points) || points.length < 2) return { primitives: [], length: 0 }
    const primitives: PathPrimitive[] = []
    let cursor = points[0]
    for (let index = 1; index < points.length - 1; index++) {
        const previous = points[index - 1]
        const point = points[index]
        const next = points[index + 1]
        const incomingLength = Math.hypot(point.x - previous.x, point.y - previous.y)
        const outgoingLength = Math.hypot(next.x - point.x, next.y - point.y)
        const radius = Math.max(0, Math.min(Math.round(finite(cornerRadius)), incomingLength / 2, outgoingLength / 2))
        if (!radius || !incomingLength || !outgoingLength) {
            const segment = line(cursor, point)
            if (segment.length) primitives.push(segment)
            cursor = point
            continue
        }
        const incoming = { x: (point.x - previous.x) / incomingLength, y: (point.y - previous.y) / incomingLength }
        const outgoing = { x: (next.x - point.x) / outgoingLength, y: (next.y - point.y) / outgoingLength }
        const cross = incoming.x * outgoing.y - incoming.y * outgoing.x
        if (Math.abs(cross) < Number.EPSILON) {
            const segment = line(cursor, point)
            if (segment.length) primitives.push(segment)
            cursor = point
            continue
        }
        const entry = { x: point.x - incoming.x * radius, y: point.y - incoming.y * radius }
        const exit = { x: point.x + outgoing.x * radius, y: point.y + outgoing.y * radius }
        const center = { x: entry.x + outgoing.x * radius, y: entry.y + outgoing.y * radius }
        const segment = line(cursor, entry)
        if (segment.length) primitives.push(segment)
        primitives.push({
            type: 'arc', center, radius,
            startAngle: Math.atan2(entry.y - center.y, entry.x - center.x),
            deltaAngle: cross >= 0 ? Math.PI / 2 : -Math.PI / 2,
            length: Math.PI * radius / 2,
        })
        cursor = exit
    }
    const finalSegment = line(cursor, points[points.length - 1])
    if (finalSegment.length) primitives.push(finalSegment)
    return { primitives, length: primitives.reduce((total, primitive) => total + primitive.length, 0) }
}

export function pointAtConnectionDistance(path: RoundedConnectionPath, distance: number): Point | null {
    if (!path.length || !path.primitives.length) return null
    let remaining = Math.max(0, Math.min(path.length, distance))
    for (const primitive of path.primitives) {
        if (remaining > primitive.length) {
            remaining -= primitive.length
            continue
        }
        const ratio = primitive.length ? remaining / primitive.length : 0
        if (primitive.type === 'line') return {
            x: primitive.from.x + (primitive.to.x - primitive.from.x) * ratio,
            y: primitive.from.y + (primitive.to.y - primitive.from.y) * ratio,
        }
        const angle = primitive.startAngle + primitive.deltaAngle * ratio
        return { x: primitive.center.x + Math.cos(angle) * primitive.radius, y: primitive.center.y + Math.sin(angle) * primitive.radius }
    }
    const last = path.primitives[path.primitives.length - 1]
    return last.type === 'line' ? last.to : pointAtConnectionDistance(path, path.length - Number.EPSILON)
}

export function connectionBulletPoints(path: RoundedConnectionPath, phase: number, bulletCount: number): Point[] {
    if (!path.length) return []
    const count = Math.max(1, Math.round(finite(bulletCount, 1)))
    const gap = path.length / count
    const offset = ((phase % path.length) + path.length) % path.length
    const result: Point[] = []
    for (let index = 0; index < count; index++) {
        const point = pointAtConnectionDistance(path, (offset + index * gap) % path.length)
        if (point) result.push(point)
    }
    return result
}
